import { useCallback, useEffect, useState } from "react";
import {
  getDevices,
  getFirmwareReleases,
  getSharedDevices,
  linkDevice,
  renameDevice,
  stopDeviceShare,
  unlinkDevice,
} from "../../services/api";
import { useSocial } from "../../contexts/SocialContext";
import {
  subscribeDevicePresence,
  subscribeDeviceState,
  subscribeOtaStatus,
} from "../../services/settingsChannel";

/**
 * Everything Settings shows about linked Khronos Timers: the device list,
 * who is online, the 1 Hz heartbeat each one streams during a session, the
 * reason a queued install is waiting, and the published firmware. One
 * subscription per stream, shared by the rows on the page and the open sheet.
 * Everything is server-pushed; nothing here polls. Linking needs an account,
 * so with `enabled` false (signed out) it fetches nothing.
 */
const useKhronosTimers = (enabled = true) => {
  const [devices, setDevices] = useState([]);
  // Timers mates have shared with you (#171): send sessions to them, nothing else.
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [online, setOnline] = useState({}); // deviceId → bool
  const [heartbeat, setHeartbeat] = useState({}); // deviceId → last state
  const [releases, setReleases] = useState([]);
  const [minRemoteVersion, setMinRemoteVersion] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [deviceList, releaseData, sharedList] = await Promise.all([
        getDevices(),
        // Releases are a nicety; a failure here shouldn't hide the devices.
        getFirmwareReleases().catch(() => null),
        getSharedDevices().catch(() => null),
      ]);
      setDevices(deviceList || []);
      if (sharedList) setShared(sharedList);
      if (releaseData) {
        setReleases(releaseData.releases || []);
        setMinRemoteVersion(releaseData.remote_install_min_version || null);
      }
    } catch {
      setError("Couldn't load your timers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) refresh();
    else {
      setDevices([]);
      setShared([]);
      setLoading(false);
    }
  }, [enabled, refresh]);

  const patchDevice = useCallback((id, fields) => {
    setDevices((prev) => prev.map((d) => (d.id === id ? { ...d, ...fields } : d)));
  }, []);

  useEffect(() => {
    const unsubState = subscribeDeviceState((evt) => {
      if (!evt?.device_id) return;
      setHeartbeat((prev) => ({ ...prev, [evt.device_id]: evt }));
      setOnline((prev) => (prev[evt.device_id] ? prev : { ...prev, [evt.device_id]: true }));
    });
    const unsubPresence = subscribeDevicePresence((evt) => {
      if (!evt?.device_id) return;
      setOnline((prev) => ({ ...prev, [evt.device_id]: evt.online === true }));
      // A device that finishes an install reboots and says hello on its new
      // build; without this the page kept showing (and judging updates
      // against) the old version until a reload.
      if (evt.firmware_version) {
        setDevices((prev) =>
          prev.some((d) => d.id === evt.device_id && d.firmware_version !== evt.firmware_version)
            ? prev.map((d) =>
                d.id === evt.device_id ? { ...d, firmware_version: evt.firmware_version } : d,
              )
            : prev,
        );
      }
      // Drop readings from a device that went away, so a stale heart rate
      // never shows as if it were current.
      if (!evt.online) {
        setHeartbeat((prev) => {
          if (!(evt.device_id in prev)) return prev;
          const next = { ...prev };
          delete next[evt.device_id];
          return next;
        });
      }
    });
    // Why a queued install can't proceed yet (#68), live: the device retries
    // for as long as the condition lasts, so the page is usually already open.
    const unsubOta = subscribeOtaStatus((evt) => {
      if (!evt?.device_id) return;
      setDevices((prev) =>
        prev.map((d) => (d.id === evt.device_id ? { ...d, ota_status: evt.status || null } : d)),
      );
    });
    return () => {
      unsubState();
      unsubPresence();
      unsubOta();
    };
  }, []);

  const link = useCallback(
    async (code) => {
      const result = await linkDevice(code.trim().toUpperCase());
      await refresh();
      return result.device;
    },
    [refresh],
  );

  const rename = useCallback(
    async (device, name) => {
      await renameDevice(device.id, name);
      patchDevice(device.id, { name });
    },
    [patchDevice],
  );

  const unlink = useCallback(async (device) => {
    await unlinkDevice(device.id);
    setDevices((prev) => prev.filter((d) => d.id !== device.id));
  }, []);

  // Give back a timer a mate shared with you.
  const leave = useCallback(async (device) => {
    await stopDeviceShare(device.share_id);
    setShared((prev) => prev.filter((d) => d.id !== device.id));
  }, []);

  // Presence only arrives as events, so a timer already online when this
  // mounted would read as offline until it next reported. The app keeps the
  // latest presence (it drives the header's device dot); start from that.
  const presence = useSocial()?.devicePresence;
  const seededOnline =
    presence?.device_id != null && online[presence.device_id] === undefined
      ? { ...online, [presence.device_id]: presence.online === true }
      : online;

  return {
    devices,
    shared,
    loading,
    error,
    online: seededOnline,
    heartbeat,
    releases,
    // Each board has its own release channel (an S3 image never runs on the
    // Korvo-1): a timer is offered and shown its own board's builds only.
    // Releases and devices from before boards count as the v2.0 (s3-v20).
    releasesFor: (device) => releasesForBoard(releases, device),
    latestFor: (device) => releasesForBoard(releases, device)[0] || null,
    minRemoteVersion,
    refresh,
    link,
    rename,
    unlink,
    leave,
  };
};

export const boardOf = (x) => (x && x.board) || "s3-v20";
export const releasesForBoard = (releases, device) =>
  releases.filter((r) => boardOf(r) === boardOf(device));

export default useKhronosTimers;
